import { Icon } from '@iconify/react';
import Link from 'next/link';
import type { FC } from 'react';

import { industryStyles } from '@/components/ResumeChecker/SelectIndustryInput';
import Text from '@/components/Text';
import { Skeleton } from '@/components/ui/skeleton';
import type {
  ExternalOffer,
  SeniorityLevelTuple,
} from '@/services/external-offers';
import { capitalizeFirstLetter } from '@/utils/cvGenerator';
import { timeAgo } from '@/utils/date';

import CompanyImage from './CompanyImage';
import OfferLocation from './OfferLocation';
import OfferSalary from './OfferSalary';

type Props = ExternalOffer & { isPreviousData: boolean };

// Matches the palette used for the seniority chips in SeniorityLevelChips.tsx.
const SENIORITY_STYLES: Record<SeniorityLevelTuple, string> = {
  INTERN: 'bg-jb-neutral-300 text-jb-neutral-700',
  JUNIOR: 'bg-jb-success-100 text-jb-success-600',
  MID: 'bg-secondary-100 text-secondary-700',
  SENIOR: 'bg-jb-primary-100 text-jb-primary-500',
};

export const getWorkTypeLabel = ({
  isHybrid,
  isOnsite,
  isRemote,
}: {
  isHybrid: boolean;
  isOnsite: boolean;
  isRemote: boolean;
}) => {
  if (isHybrid) return 'Hybrid';
  if (isOnsite) return 'On-site';
  if (isRemote) return 'Remote';
  return null;
};

const Offer: FC<Props> = ({
  jobTitle,
  locations,
  companyName,
  id,
  isPreviousData,
  salaryLower,
  salaryUpper,
  salaryPeriod,
  salaryCurrency,
  isHybrid,
  isOnsite,
  isRemote,
  companyImage,
  createdAt,
  industry,
  seniorityLevel,
}) => {
  const workType = getWorkTypeLabel({ isHybrid, isOnsite, isRemote });
  const industryStyle = industryStyles.find(
    (style) => style.industry === industry
  );

  return (
    <Link
      className="relative block h-full min-w-0 rounded-2xl shadow transition-shadow hover:shadow-md"
      href={{
        pathname: '/jobs/[id]',
        query: { id: id.toString() },
      }}
    >
      {isPreviousData && (
        <Skeleton className="absolute left-0 top-0 h-full w-full rounded-2xl" />
      )}
      <div className="z-10 flex h-full w-full min-w-0 flex-col justify-between gap-3 rounded-2xl border border-jb-neutral-200 bg-jb-neutral-100 p-5 text-jb-neutral-1000 shadow-big-moduls">
        <div className="flex w-full flex-col-reverse items-start gap-2">
          <div className="flex w-full min-w-0 flex-row gap-2">
            <CompanyImage
              src={companyImage?.url}
              placeholderIconProps={{ fontSize: 28 }}
              imageContainerClassName="h-9 w-9"
              height={companyImage?.height ?? 0}
              width={companyImage?.width ?? 0}
            />
            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <Text
                variant="jb-body-large"
                title={jobTitle}
                className="truncate font-bold text-jb-neutral-1000"
              >
                {jobTitle}
              </Text>
              <Text
                variant="jb-body-medium"
                title={companyName}
                className="truncate text-jb-neutral-800"
              >
                {companyName}
              </Text>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {seniorityLevel && (
              <div
                className={`flex items-center rounded-full px-2.5 py-1 ${SENIORITY_STYLES[seniorityLevel]}`}
              >
                <Text variant="jb-body-xsmall" className="font-semibold">
                  {capitalizeFirstLetter(seniorityLevel)}
                </Text>
              </div>
            )}
            {industryStyle && (
              <div className="flex items-center gap-1 rounded-full bg-jb-neutral-200 px-2.5 py-1">
                <Icon
                  icon={industryStyle.icon}
                  className={industryStyle.iconColor}
                  fontSize={14}
                />
                <Text
                  variant="jb-body-xsmall"
                  className="font-semibold text-jb-neutral-800"
                >
                  {industryStyle.label}
                </Text>
              </div>
            )}
          </div>
        </div>

        <div className="flex w-full items-end justify-between gap-2">
          <div className="flex min-w-0 flex-col items-start gap-1">
            <OfferLocation locations={locations} workType={workType} />
            <OfferSalary
              format="k"
              salaryLower={salaryLower}
              salaryUpper={salaryUpper}
              salaryPeriod={salaryPeriod}
              currency={salaryCurrency}
            />
          </div>
          <Text
            variant="jb-body-small"
            className="shrink-0 text-jb-neutral-700"
          >
            {timeAgo(new Date(createdAt))}
          </Text>
        </div>
      </div>
    </Link>
  );
};

export default Offer;
